import { useEffect, useState } from 'react';
import api, { formatApiError, fileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Receipt, Plus, FileText, Check, X, IndianRupee, Calendar, Upload, BadgeCheck } from 'lucide-react';
import FileUploader from '../components/FileUploader';

const CATEGORIES = [
  { v: 'travel', label: 'Travel' },
  { v: 'medical', label: 'Medical' },
  { v: 'data', label: 'Data / Internet' },
  { v: 'food', label: 'Food / Meals' },
  { v: 'stationery', label: 'Stationery' },
  { v: 'other', label: 'Other' },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const statusStyles = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  credited: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function Reimbursements() {
  const { user } = useAuth();
  const isHr = ['admin', 'hr'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category: 'travel', amount: '', description: '',
    expense_date: new Date().toISOString().slice(0, 10),
    invoice_file_ids: [],
  });

  const load = async () => {
    try {
      const { data } = await api.get('/reimbursements');
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.amount || +form.amount <= 0) return toast.error('Enter a valid amount');
    if (!form.description.trim()) return toast.error('Add a short description');
    if (form.invoice_file_ids.length === 0) return toast.error('Upload at least one invoice');
    setSaving(true);
    try {
      await api.post('/reimbursements', { ...form, amount: +form.amount });
      toast.success('Reimbursement request submitted — HR has been notified');
      setOpen(false);
      setForm({ category: 'travel', amount: '', description: '', expense_date: new Date().toISOString().slice(0, 10), invoice_file_ids: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const decide = async (rid, action) => {
    const note = action === 'reject' ? window.prompt('Reason for rejection?') : '';
    if (action === 'reject' && note === null) return;
    try {
      await api.patch(`/reimbursements/${rid}/decision?action=${action}`, { note: note || null });
      toast.success(`Marked as ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'credited'}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byStatus = (s) => items.filter(i => i.status === s);
  const total = (s) => byStatus(s).reduce((a, b) => a + (b.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="reimbursements-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Expenses</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Reimbursements</h1>
          <p className="text-slate-500 mt-1">
            {isHr ? `${items.length} request${items.length !== 1 ? 's' : ''} · Review and credit employee expenses. You can also submit your own.` :
                    'Submit travel, medical and other expense bills here.'}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-reimbursement-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.8} /> Submit a reimbursement
            </Button>
          </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-reimbursement-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">New reimbursement</DialogTitle>
                <p className="text-sm text-slate-500 mt-1">HR will review your bill and credit it once approved.</p>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Category *</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="reimb-category" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Amount (₹) *</Label>
                    <Input data-testid="reimb-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold text-slate-600">Expense date</Label>
                    <Input data-testid="reimb-date" type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Description *</Label>
                  <Textarea data-testid="reimb-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" rows={3} placeholder="What is this expense for?" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Invoices / bills *</Label>
                  <div className="mt-1 space-y-2">
                    {form.invoice_file_ids.map((id, i) => (
                      <div key={id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid={`reimb-invoice-${i}`}>
                        <a href={fileUrl(id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#3D4A8E] truncate"><FileText className="w-4 h-4" /> Invoice {i + 1}</a>
                        <button onClick={() => setForm({ ...form, invoice_file_ids: form.invoice_file_ids.filter(x => x !== id) })} className="text-red-500 text-xs"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <FileUploader label="" fileId={null} testId="reimb-invoice-upload"
                                  onUploaded={(fid) => setForm(f => ({ ...f, invoice_file_ids: [...f.invoice_file_ids, fid] }))} />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-reimbursement" onClick={submit} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {saving ? 'Submitting…' : 'Submit request'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </div>

      {isHr && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['pending', 'approved', 'credited', 'rejected'].map(s => (
            <div key={s} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{s}</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{inr(total(s))}</div>
              <div className="text-xs text-slate-500 mt-0.5">{byStatus(s).length} request{byStatus(s).length !== 1 ? 's' : ''}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="all">
        <TabsList className="bg-white border border-slate-200 rounded-xl p-1">
          {['all', 'pending', 'approved', 'credited', 'rejected'].map(s => (
            <TabsTrigger key={s} value={s} data-testid={`reimb-tab-${s}`} className="capitalize">{s}</TabsTrigger>
          ))}
        </TabsList>
        {['all', 'pending', 'approved', 'credited', 'rejected'].map(s => (
          <TabsContent key={s} value={s} className="mt-4 space-y-3">
            {(s === 'all' ? items : byStatus(s)).length === 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400" data-testid={`empty-${s}`}>
                <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No {s === 'all' ? '' : s} reimbursements.
              </div>
            )}
            {(s === 'all' ? items : byStatus(s)).map(r => (
              <div key={r.id} data-testid={`reimb-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-slate-900">{inr(r.amount)}</span>
                      <span className="text-xs uppercase tracking-wider font-bold text-slate-500 capitalize">· {r.category}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${statusStyles[r.status]}`}>{r.status}</span>
                    </div>
                    {isHr && <div className="text-xs text-slate-500 mt-1">{r.employee_name} · {r.employee_email}</div>}
                    <div className="text-sm text-slate-700 mt-2">{r.description}</div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Expense {r.expense_date}</span>
                      <span>· Submitted {new Date(r.submitted_at).toLocaleDateString()}</span>
                    </div>
                    {r.invoice_file_ids?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {r.invoice_file_ids.map((fid, i) => (
                          <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" data-testid={`reimb-${r.id}-invoice-${i}`}
                             className="inline-flex items-center gap-1.5 text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1 text-[#3D4A8E]">
                            <FileText className="w-3 h-3" /> Invoice {i + 1}
                          </a>
                        ))}
                      </div>
                    )}
                    {r.decision_note && (
                      <div className="mt-2 text-xs text-slate-500 italic">HR note: {r.decision_note}</div>
                    )}
                  </div>
                  {isHr && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {r.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => decide(r.id, 'approve')} data-testid={`reimb-approve-${r.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"><Check className="w-3 h-3 mr-1" /> Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => decide(r.id, 'reject')} data-testid={`reimb-reject-${r.id}`} className="rounded-full h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"><X className="w-3 h-3 mr-1" /> Reject</Button>
                        </>
                      )}
                      {r.status === 'approved' && (
                        <Button size="sm" onClick={() => decide(r.id, 'credit')} data-testid={`reimb-credit-${r.id}`} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] h-8 text-xs"><IndianRupee className="w-3 h-3 mr-1" /> Mark as credited</Button>
                      )}
                      {r.status === 'credited' && <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Credited</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
